<template>
  <div v-if="loading">
    <v-card shaped>
      <v-skeleton-loader type="table"></v-skeleton-loader>
    </v-card>
  </div>
  <div v-else>
    <v-card shaped>
      <v-card-title>
        <v-row class="d-flex align-center">
          <v-col cols="8">
            Acknowledgements
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <v-switch 
              v-model="expandAll"
              inset
              label="Expand all"
            ></v-switch>
          </v-col>
        </v-row>
      </v-card-text>
      <div class="d-flex align-center">
        <div 
          class="progress awaiting"
          :style='{ 
            width: progress.awaiting.percentage,
            backgroundColor: progress.awaiting.color
          }'
        ></div>
        <div
          class="progress incomplete"
          :style='{ 
            width: progress.incomplete.percentage,
            backgroundColor: progress.incomplete.color
          }'
        ></div>
        <div 
          class="progress complete"
          :style='{ 
            width: progress.complete.percentage,
            backgroundColor: progress.complete.color
          }'
        ></div>
      </div>
      <v-expansion-panels
        v-model="visiblePanels"
        flat
        multiple
      >
        <v-expansion-panel
          v-for="(panel,i) in panels"
          :key="i"
        >
          <v-expansion-panel-header>
            <v-row>
              <v-col class="grow">
                <h4>
                  {{ panel.title }}
                  <v-badge
                    class="pl-1"
                    :color="panel.color"
                    :content="panel.count"
                  ></v-badge>
                </h4>
              </v-col>
              <v-col class="shrink">
                <component
                  v-if="panel.btnComponent"
                  v-bind:is="panel.btnComponent"
                  :acknowledgements="panel.acknowledgements"
                  :session="session"
                ></component>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <AcknowledgementsTable
              :acknowledgements="panel.acknowledgements"
              :search="search"
              :type="panel.tableType"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  </div>
</template>

<script>
  import AcknowledgementsTable from '@/components/acknowledgement/Table'
  import DownloadAcknowledgements from '@/components/acknowledgement/Download'
  import SendRequests from '@/components/acknowledgement/SendRequests'
  import SendReminders from '@/components/acknowledgement/SendReminders'

  export default {
    props: {
      loading: Boolean,
      session: Object
    },
    components: {
      AcknowledgementsTable,
      DownloadAcknowledgements,
      SendRequests,
      SendReminders
    },
    name: 'AcknowledgementCard',
    data () {
      return {
        expandAll: false,
        search: ''
      }
    },
    created () {
      this.$store.dispatch('getRecords', {
        table: 'Acknowledgements',
        view: 'All Acknowledgements',
        dispatch: 'setAcknowledgements'
      })
    },
    computed: {
      acknowledgements () {
        if (this.employees.length > 0) {
          return this.$store.getters.getAcknowledgements
                 .filter(a => {
                   return a.fields.Session[0] === this.session.id
                 })
                 .map(a => {
                    a.session = this.session
                    a.employee = this.employees.find(e => {
                      return e.id === a.fields.Employee[0]
                    })
                    return a
                  })
        } else {
          return []
        }
      },
      awaitingRequests () {
        return this.employees.filter(e => {
          const esChkdIn = this.session.fields['Checked In']
          const employeeCheckedIn = esChkdIn ? esChkdIn.includes(e.id) : false
          
          const noAcknowledgementReq = this.acknowledgements.filter(a => {
            return a.employee.id ? a.employee.id === e.id : false
          }).length === 0
          
          const isAwaitingRequest = employeeCheckedIn && noAcknowledgementReq
      
          return isAwaitingRequest
        }).map(e => {
          // put employee data in the same place we put it for acknowledgements
          // so that we can reuse the table component
          e.employee = {
            id: e.id,
            fields: e.fields
          }
          return e
        })
      },
      completeAcks () {
        return this.acknowledgements.filter(a => {
          return a.fields.Acknowledged
        })
      },
      employees () {
        return this.$store.getters.getEmployees
      },
      incompleteAcks () {
        return this.acknowledgements.filter(a => {
          return !a.fields.Acknowledged
        })
      },
      panels () {
        return [
          {
            title: 'Awaiting Requests',
            btnComponent: 'SendRequests',
            btnIcon: 'mdi-send',
            btnText: 'Send requests',
            acknowledgements: this.awaitingRequests,
            tableType: 'awaiting',
            count: this.awaitingRequests.length || 0,
            color: this.$vuetify.theme.themes.light.secondary
          },
          {
            title: 'Incomplete',
            btnComponent: 'SendReminders',
            btnIcon: 'mdi-send',
            btnText: 'Send nudges',
            acknowledgements: this.incompleteAcks,
            tableType: 'incomplete',
            count: this.incompleteAcks.length || 0,
            color: this.$vuetify.theme.themes.light.primary
          },
          {
            title: 'Complete',
            btnComponent: 'DownloadAcknowledgements',
            btnIcon: 'mdi-cloud-download',
            btnText: 'Download acknowledgements',
            acknowledgements: this.completeAcks,
            tableType: 'complete',
            count: this.completeAcks.length || 0,
            color: this.$vuetify.theme.themes.light.success
          }
        ]
      },
      progress () {
        let results = {
          awaiting: {
            percentage: '0%',
            color: this.$vuetify.theme.themes.light.secondary
          },
          incomplete: {
            percentage: '0%',
            color: this.$vuetify.theme.themes.light.primary
          },
          complete: {
            percentage: '0%',
            color: this.$vuetify.theme.themes.light.success
          }
        }
        
        const numChckdIn = this.session.fields['Checked In'] ?
                             this.session.fields['Checked In'].length : 0
        
        if (numChckdIn === 0) {
          return results
        }
        
        const percentage = (num) => {
          return num / numChckdIn * 100 + '%'
        } 
        
        results.awaiting.percentage = percentage(this.awaitingRequests.length)
        results.incomplete.percentage = percentage(this.incompleteAcks.length)
        results.complete.percentage = percentage(this.completeAcks.length)
        
        return results
      },
      visiblePanels: {
        get () {
          if (this.expandAll) {
            return this.panels.map((p, i) => {
              return i
            })
          }
          return []
        },
        set (newVal) {
          return newVal
        }
      }
    }
  }
</script>

<style scoped>
  .progress {
    height: 4px;
  }
</style>
